import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { NewPermission } from '@core/enums';
import { DirectivesModule } from '@shared/directives/directives.module';
import { TasksCrmPanelModule } from '@shared/modules/legacy-tasks/tasks-crm-panel/tasks-crm-panel.module';
import { TsButtonModule } from 'tsui';
import { TsComponent } from '../../ts.component';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { Entity } from 'app/modules/jupiter/types';
import { forkJoin, map, noop, Observable, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { Task } from 'app/shared/modules/tasks/tasks.types';
import { ofActionCompleted } from '@ngxs/store';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import { TaskExportDialogResult } from '@shared/modules/legacy-tasks/task-export-dialog/task-export-dialog.component';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { EntityActions } from 'app/modules/jupiter/@store/actions';

@Component({
  selector: 'app-crm-task',
  standalone: true,
  imports: [
    TsButtonModule,
    DirectivesModule,
    TasksCrmPanelModule,
    AsyncPipe,
    RouterModule,
    MatSidenavModule,
    RouterModule,
  ],
  templateUrl: './crm-task.component.html',
  styleUrl: './crm-task.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterCrmTaskTabComponent extends TsComponent implements OnInit {
  taskExportUtilsService = inject(TaskExportUtilsService);
  legacyTasksTabService = inject(LegacyTasksTabService);

  NewPermission = NewPermission;
  entity: Entity;

  entity$: Observable<Entity> = this.store.select(JupiterState.getEntity).pipe(
    takeUntilDestroyed(this.destroyRef),
    tap((entity) => {
      if (entity) {
        this.entity = entity;
        this.store.dispatch(new EntityActions.TaskActions.Load());
      }
    }),
  );

  tasks$: Observable<Task[]> = this.store.select(JupiterState.getTasksItems);
  opened$: Observable<boolean>;

  @ViewChild('matDrawer') matDrawer: MatDrawer;

  ngOnInit(): void {
    this.layoutService.emitCommon('crm-tasks', false, 'behaviorSubject');

    this.opened$ = this.layoutService.getCommon('crm-tasks').pipe(map((value) => value === true));

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(EntityActions.TaskActions.Add),
        tap(() => {
          this.openTaskDetails(this.store.selectSnapshot(JupiterState.getTransient).task);
        }),
      )
      .subscribe(noop);
  }

  openTaskExportDialog() {
    this.taskExportUtilsService
      .openTaskExportDialog()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((format: TaskExportDialogResult) => {
          return forkJoin([
            this.legacyTasksTabService.getTasks(
              this.entity.task_list.id,
              null,
              {},
              format,
              false,
              TaskExportUtilsService.crmDefaultSelectColumns,
            ),
            format,
          ]);
        }),
        tap(([data, format]: [any, TaskExportDialogResult]) =>
          this.taskExportUtilsService.handleTaskExport(data, format, this.entity.name),
        ),
      )
      .subscribe(noop);
  }

  createTask(): void {
    const task: Task = {
      task_type: 'task',
      task_list_id: this.entity.task_list.id,
      workflow_state: 'not_started',
    };

    this.store.dispatch(new EntityActions.TaskActions.Add(task));
  }

  openTaskDetails(task: Task): void {
    this.router
      .navigate([{ outlets: { task: [task.task_list_id, task.id] } }], { relativeTo: this.activatedRoute })
      .then(() => {
        this.layoutService.emitCommon('crm-tasks', true);
      });
  }

  drawerClosed(): void {
    this.store.dispatch(new EntityActions.TaskActions.Load());
    this.router.navigate(['../crm-tasks'], { relativeTo: this.activatedRoute });
  }
}
